import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  CUSTOM_DATE_PERIOD,
  useManagedAccountPageContext,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Context/ManagedAccountPageContext';
import DataContainer from 'Components/UI/DataContainer';
import Content
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/ManagedAccountTransactionTabPanelLayout/Content';
import {
  FDHeadlineText, FDSelect, FDStack, Table,
} from '@facet-wealth/design';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Button from 'Components/UI/Button';
import Typography from 'Components/UI/Typography';
import useMobileLayout from 'Hooks/useMobileLayout';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import fr from 'facet-rest';
import FAQButton from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/FAQButton';

const useStyles = buildUseStyles('ManagedAccountTransactionTabPanelLayout', (theme) => ({
  root: {
    ...getSectionCardStyle({ theme }),
  },
  tableMoreButton: {
    display: 'flex',
    justifyContent: 'center',
  },
  noDataText: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

export const MANAGED_ACCOUNT_ACTIVITY_TABLE_MOBILE_FIELDS = ['date', 'amount', 'status'];


const ManagedAccountTransactionTabPanelLayout = ({
  className,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const {
    managedAccountActivityCollection, transactionDatePeriod, transactionType, managedAccountActivityLoadingError, setTransactionType, setTransactionDatePeriod,
    transactionStartDate, transactionEndDate, setTransactionStartDate, setTransactionEndDate, currentTabResource, increaseTransactionLimit, managedAccountActivityLoading,
    managedAccountPageResource,
  } = useManagedAccountPageContext();


  const transactionDatePeriodMeta = managedAccountActivityCollection?.meta()
    .query('getManagedAccountActivity')
    .field('datePeriod');

  const transactionStartDateMeta = managedAccountActivityCollection?.meta()
    .query('getManagedAccountActivity')
    .field('startDate');

  const transactionEndDateMeta = managedAccountActivityCollection?.meta()
    .query('getManagedAccountActivity')
    .field('endDate');

  const transactionTypeMeta = managedAccountActivityCollection?.meta()
    .query('getManagedAccountActivity')
    .field('transactionType');

  const handleTransactionTypeChange = async (e) => {
    e.preventDefault();
    await setTransactionType(e.target.value);
  };

  const handleTransactionDatePeriodChange = async (e) => {
    e.preventDefault();
    await setTransactionDatePeriod(e.target.value);
  };

  const showStartAndEndDate = transactionDatePeriod === CUSTOM_DATE_PERIOD;

  const handleTransactionStartDateChange = async (newStartDate) => {
    if (transactionDatePeriod === CUSTOM_DATE_PERIOD) {
      await setTransactionStartDate(newStartDate);
    }
  };

  const handleTransactionEndDateChange = async (newEndDate) => {
    if (transactionDatePeriod === CUSTOM_DATE_PERIOD) {
      await setTransactionEndDate(newEndDate);
    }
  };

  return (
    <Box
      className={classNames(classes.root, className)}
    >
        <>
          <FDStack direction={'row'}>
            <FDHeadlineText value={currentTabResource?.attr('name')} />
            {!!managedAccountPageResource.relationship(fr.relationships.faq.name) && <>
              <FAQButton link={managedAccountPageResource.relationship(fr.relationships.faq.name).link('related')} />
            </>}
          </FDStack>
          <br />
          {
            managedAccountActivityCollection
              ? (
                <Table.FilterSet direction={'row'}>
                  <FDSelect
                    label={transactionTypeMeta?.attr('prompt')}
                    options={transactionTypeMeta?.options()
                      .list()
                      .map(option => {
                        return {
                          label: option.attr('prompt'),
                          value: option.attr('id'),
                        };
                      })}
                    value={transactionType}
                    onChange={handleTransactionTypeChange}
                  />
                  <FDSelect
                    label={transactionDatePeriodMeta?.attr('prompt')}
                    options={transactionDatePeriodMeta?.options()
                      .list()
                      .map(option => {
                        return {
                          label: option.attr('prompt'),
                          value: option.attr('id'),
                        };
                      })}
                    value={transactionDatePeriod}
                    onChange={handleTransactionDatePeriodChange}
                  />
                  { showStartAndEndDate
                    ? (<Table.FilterDateRange
                      label={transactionStartDateMeta?.attr('prompt')}
                      value={transactionStartDate}
                      onChange={handleTransactionStartDateChange}
                    />) : null
              }
                  { showStartAndEndDate
                    ? (
                      <Table.FilterDateRange
                        label={transactionEndDateMeta?.attr('prompt')}
                        value={transactionEndDate}
                        onChange={handleTransactionEndDateChange}
                      />) : null
              }
                </Table.FilterSet>) : null
          }
        </>
      <Table.Table>
        <Table.HeadColumnHeaderSet>
          {
              managedAccountActivityCollection?.meta().template().list().filter((field) => (isMobile ? MANAGED_ACCOUNT_ACTIVITY_TABLE_MOBILE_FIELDS.includes(field.attr('name')) : true))
                .filter(field => !field.attr('hidden'))
                .map(field => {
                  const prompt = field.attr('prompt');
                  if (field.attr('type') === 'number') {
                    return <Table.HeadColumnHeaderCellNumber value={prompt} />;
                  }
                  return <Table.HeadColumnHeaderCell value={prompt} />;
                })
            }
        </Table.HeadColumnHeaderSet>
        <DataContainer
          className={classNames(className, classes.root)}
          component={Content}
          componentProps={{
            managedAccountActivityCollection,
          }}
          loading={!managedAccountActivityCollection && !managedAccountActivityLoadingError}
          loadingError={managedAccountActivityLoadingError}
        />
      </Table.Table>
      {/* Typography will be replaced by a Facet Design Library component once the latest version is bundled */}
      {!managedAccountActivityLoadingError && managedAccountActivityCollection?.resources()?.list().length === 0 ? (
        <Typography variant="h4" weight="medium" className={classes.noDataText}>
          No Transactions Found
        </Typography>
      ) : null
      }
      {managedAccountActivityCollection?.link('next') ? (
        <Box className={classes.tableMoreButton}>
          <Button round color={'primaryBlue'} variant={'contained'} onClick={() => increaseTransactionLimit()} disabled={managedAccountActivityLoading}>{'More'}</Button>
        </Box>
      ) : null}
    </Box>
  );
};

ManagedAccountTransactionTabPanelLayout.defaultProps = {
  className: '',
};

ManagedAccountTransactionTabPanelLayout.propTypes = {
  className: PropTypes.string,
};

export default ManagedAccountTransactionTabPanelLayout;
